import type React from "react";
import { twMerge } from "tailwind-merge";
import { CopyButton } from "@/ui/copy-button";

type CodeBlockSize = "xs" | "sm" | "md";
type CodeBlockWrap = "hard" | "soft";

const sizeClasses: Record<CodeBlockSize, string> = {
  xs: "max-h-20 sm:max-h-24",
  sm: "max-h-40 sm:max-h-48",
  md: "max-h-50 sm:max-h-60",
};

const wrapClasses: Record<CodeBlockWrap, string> = {
  hard: "whitespace-pre-wrap break-all sm:break-normal",
  soft: "whitespace-pre-wrap break-words",
};

export type CodeBlockProps = {
  size?: CodeBlockSize;
  wrap?: CodeBlockWrap;
  className?: string;
  copy?: boolean;
  copyValue?: string;
  copySubject?: string;
  children: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<"pre">, "className">;

export function CodeBlock({
  size = "sm",
  wrap = "hard",
  className,
  copy,
  copyValue,
  copySubject,
  children,
  ...props
}: CodeBlockProps) {
  const resolvedCopyValue =
    copyValue ?? (typeof children === "string" ? children : null);

  const pre = (
    <pre
      {...props}
      className={twMerge(
        "max-w-full overflow-x-auto overflow-y-auto rounded bg-ui-surface-code/90 p-3 font-mono text-[10px] leading-tight text-ui-code-block-fg sm:text-[11px] dark:bg-ui-surface-code",
        sizeClasses[size],
        wrapClasses[wrap],
        copy && resolvedCopyValue ? "pr-14" : undefined,
        className,
      )}
    >
      {children}
    </pre>
  );

  if (!copy || !resolvedCopyValue) return pre;

  return (
    <div className="relative min-w-0">
      <div className="absolute right-2 top-2 z-10">
        <CopyButton
          value={resolvedCopyValue}
          subject={copySubject}
          variant="codeOverlay"
        />
      </div>
      {pre}
    </div>
  );
}
